<template>
    <div class="sites-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>Müşteri Aktiviteleri</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <StackedAreaChart
            title="Müşteri Sayısı"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-if="!tableMode"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard :title="$t('report.total')" :value="stats.TotalSiteCount" unit="site" />
            <StatCard :title="$t('report.average')" :value="stats.AverageSiteCount" unit="site" />
            <StatCard :title="$t('report.max')" :value="stats.MaxSiteCount" unit="site" />
            <StatCard :title="$t('report.min')" :value="stats.MinSiteCount" unit="site" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { ADMIN_REPORT_API, REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";

const log = log2("SitesChart");
export default Vue.extend({
    name: "SitesChart",
    components: { ChartDoddleIcon, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.customerCount")],
            stats: {
                TotalSiteCount: 0,
                AverageSiteCount: 0,
                MaxSiteCount: 0,
                MinSiteCount: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".sites-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            ADMIN_REPORT_API.siteCounts(this.filters)
                .then((res) => {
                    log("siteCounts data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    key: "date",
                    field: "date",
                    title: this.$t("chatbotv2.date"),
                },
                {
                    key: "siteCount",
                    field: "siteCount",
                    title: this.$t("report.siteCount"),
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getRows.buckets", buckets);
            const rows = buckets.map((bucket) => {
                const siteCount = bucket.siteCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    siteCount,
                };
            });
            return rows;
        },
        getStats() {
            const buckets = this.graphData?.buckets || [];
            log("getStats.buckets", buckets);
            const SiteCounts = buckets.map((x) => x.siteCount);
            const isEmpty = SiteCounts.length === 0;
            const TotalSiteCount = isEmpty ? 0 : _.chain(SiteCounts).sum().value();
            const AverageSiteCount = isEmpty ? 0 : parseInt(_.mean(SiteCounts).toFixed(1));
            const MaxSiteCount = isEmpty ? 0 : parseInt(_.max(SiteCounts).toFixed(1));
            const MinSiteCount = isEmpty ? 0 : parseInt(_.min(SiteCounts).toFixed(1));

            return {
                TotalSiteCount,
                AverageSiteCount,
                MaxSiteCount,
                MinSiteCount,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getSeries.buckets", buckets);
            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => bucket.siteCount);
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: "rgb(55, 162, 255)",
                            },
                            {
                                offset: 1,
                                color: "rgb(255,255,255)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            log("exportToExcel.buckets", buckets);
            const data = buckets.map((bucket) => {
                const siteCount = bucket.siteCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    siteCount,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam site",
                    siteCount: stats.TotalSiteCount,
                },
                {
                    date: "Ortalama site",
                    siteCount: stats.AverageSiteCount,
                },
                {
                    date: "Maksimum site",
                    siteCount: stats.MaxSiteCount,
                },
                {
                    date: "Minimum site",
                    siteCount: stats.MinSiteCount,
                },
            ];
            expertToExcel(excelData, `site_count_timeseries.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".sites-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.sites-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.sites-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
